import { useCallback, useRef } from 'react'
import type { PropertiesPanelSectionId } from '@/lib/panels/types/properties-panel-section-id'

/**
 * Properties Panel scrolling: brings the relevant section to the top of the panel on open or
 * on a selection change, honoring the Layout/Effects pin — see `usePropertiesPanelScroll`.
 * Owned by `LeftPanel` (not inside `SelectTool`) for the same reason the auto-open hook is: the
 * scroll container spans `SelectTool`'s own unmount/remount when switching tool tabs.
 *
 * Holds the scroll container ref plus each section's DOM node, registered by `SelectTool`.
 */
export function useSectionElementRegistry() {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const sectionElsRef = useRef<Partial<Record<PropertiesPanelSectionId, HTMLDivElement | null>>>({})
  const registerSectionElement = useCallback(
    (id: PropertiesPanelSectionId, el: HTMLDivElement | null) => {
      sectionElsRef.current[id] = el
    },
    []
  )
  const getSectionElement = useCallback(
    (id: PropertiesPanelSectionId) => sectionElsRef.current[id] ?? null,
    []
  )

  return { scrollContainerRef, registerSectionElement, getSectionElement }
}
